import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Check, Eye, EyeOff, Lock } from 'lucide-react'

export const MIN_PASSWORD_LENGTH = 8

export function ModalHeader(): ReactElement {
  const { t } = useTranslation('auth')
  return (
    <div className="border-b border-slate-200 bg-amber-50 px-6 py-5 dark:border-slate-800 dark:bg-amber-950/30">
      <div className="flex items-center gap-3">
        <div className="rounded-full bg-amber-100 p-2 dark:bg-amber-900/50">
          <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h2
            className="text-xl font-bold text-slate-900 dark:text-slate-100"
            style={{ fontFamily: 'var(--font-display, Syne, sans-serif)' }}
          >
            {t('passwordChange.title')}
          </h2>
          <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
            {t('passwordChange.subtitle')}
          </p>
        </div>
      </div>
    </div>
  )
}

export function ErrorBanner({ error }: { error: string | null }): ReactElement | null {
  if (!error) return null
  return (
    <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30">
      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
      <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
    </div>
  )
}

interface PasswordFieldProps {
  id: string
  label: string
  placeholder: string
  autoComplete: 'current-password' | 'new-password'
  value: string
  onChange: (value: string) => void
  showPassword: boolean
  onToggleShow: () => void
  hasError?: boolean
}

export function PasswordField({
  id,
  label,
  placeholder,
  autoComplete,
  value,
  onChange,
  showPassword,
  onToggleShow,
  hasError = false,
}: PasswordFieldProps): ReactElement {
  const borderClass = hasError
    ? 'border-red-400 dark:border-red-600'
    : 'border-slate-300 dark:border-slate-700'
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => {
            onChange(e.target.value)
          }}
          className={`w-full rounded-lg border px-4 py-2.5 pr-10 ${borderClass} bg-white text-slate-900 transition-all focus:border-transparent focus:ring-2 focus:ring-fuchsia-500 focus:outline-none dark:bg-slate-800 dark:text-slate-100`}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute top-1/2 right-3 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  )
}

export function MinLengthIndicator({ valid }: { valid: boolean }): ReactElement {
  const { t } = useTranslation('auth')
  const colourClass = valid
    ? 'text-green-600 dark:text-green-400'
    : 'text-slate-500 dark:text-slate-400'
  return (
    <div className="mt-2 flex items-center gap-2 text-xs">
      <span className={`flex items-center gap-1 ${colourClass}`}>
        {valid ? (
          <Check className="h-3 w-3" />
        ) : (
          <span className="h-3 w-3 rounded-full border border-current" />
        )}
        {t('passwordChange.minLength', { count: MIN_PASSWORD_LENGTH })}
      </span>
    </div>
  )
}

export function MismatchMessage(): ReactElement {
  const { t } = useTranslation('auth')
  return (
    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
      {t('passwordChange.passwordMismatch')}
    </p>
  )
}

export function SubmitButton({
  disabled,
  submitting,
}: {
  disabled: boolean
  submitting: boolean
}): ReactElement {
  const { t } = useTranslation('auth')
  return (
    <div className="pt-4">
      <button
        type="submit"
        disabled={disabled}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-fuchsia-600 px-6 py-3 font-medium text-white transition-colors hover:bg-fuchsia-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-slate-700"
      >
        {submitting ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            {t('passwordChange.submittingButton')}
          </>
        ) : (
          <>
            <Lock className="h-4 w-4" />
            {t('passwordChange.submitButton')}
          </>
        )}
      </button>
    </div>
  )
}

export function BlockingMessage(): ReactElement {
  const { t } = useTranslation('auth')
  return (
    <p className="text-center text-xs text-slate-500 dark:text-slate-400">
      {t('passwordChange.blockingMessage')}
    </p>
  )
}

/**
 * Sign out from the forced password-change screen.
 *
 * The modal is deliberately non-dismissable, which is right — the change must not be
 * skipped. But without this, "cannot be dismissed" meant "cannot be left": the session
 * lives in localStorage, so refreshing returns straight to the modal and the only exit
 * is clearing browser storage by hand. Anyone who signed in as the wrong user — an
 * administrator checking a newly issued account, most obviously — was locked out of
 * their own until they knew that trick.
 *
 * Signing out does not skip the requirement: the flag is on the account, so the modal
 * reappears at the next sign-in.
 */
export function SignOutLink({ onSignOut }: { onSignOut: () => void }): ReactElement {
  const { t } = useTranslation('auth')
  return (
    <button
      type="button"
      onClick={onSignOut}
      className="w-full text-center text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
    >
      {t('passwordChange.signOut')}
    </button>
  )
}
